import { lazy, ComponentType } from 'react'

export const CHUNK_RELOAD_KEY = 'chunk_reload_attempted'

/**
 * Detects if an error is related to dynamic chunk/module loading failures.
 * These typically occur when:
 * - A new deployment invalidates old chunk hashes
 * - Network issues prevent chunk loading
 * - Browser cache serves stale references
 */
export function isChunkLoadError(error: Error): boolean {
  const chunkErrorPatterns = ['Loading chunk', 'Importing a module script failed', 'Failed to fetch dynamically imported module', 'Unable to preload CSS', 'Loading CSS chunk']

  return error.name === 'ChunkLoadError' || chunkErrorPatterns.some((pattern) => error.message.includes(pattern))
}

/**
 * Retries an async function with exponential backoff.
 * Exported for testing purposes.
 *
 * @param fn - The async function to retry
 * @param retries - Number of retry attempts
 * @returns The result of the function
 */
export async function retryWithBackoff<T>(fn: () => Promise<T>, retries: number): Promise<T> {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (error) {
      if (i === retries - 1) throw error
      // Wait before retry (exponential backoff: 1s, 2s, etc.)
      await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)))
    }
  }
  return fn()
}

/**
 * Wraps React.lazy with retry logic for transient network failures.
 * Uses exponential backoff between retries.
 *
 * @param importFn - The dynamic import function
 * @param retries - Number of retry attempts (default: 2)
 * @returns A lazy component that will retry on failure
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(importFn: () => Promise<{ default: T }>, retries = 2): React.LazyExoticComponent<T> {
  return lazy(() => retryWithBackoff(importFn, retries))
}

/**
 * Attempts to recover from a chunk load error by reloading the page once.
 * Uses sessionStorage to prevent infinite reload loops.
 *
 * @returns true if a reload was triggered, false otherwise
 */
export function attemptChunkErrorRecovery(): boolean {
  const hasReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY)

  if (!hasReloaded) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true')
    window.location.reload()
    return true
  }

  // Clear the flag so future chunk errors can trigger reload
  sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  return false
}
